/* Hand-maintained read side of the connector brand-mark registry.
 *
 * Three modules, one job each:
 *   ./connector-brand-svgs.generated.ts  pure thesvg data, overwritten wholesale
 *   ./connector-brand-overrides.ts       hand-fixed marks
 *   this file                            merge, cruft-strip, and the public API
 *
 * Keeping data and policy separate makes source refreshes mechanical while
 * preserving the aliases, cleanup, and theme corrections below. */

import { CONNECTOR_BRAND_OVERRIDES } from "./connector-brand-overrides";
import { CONNECTOR_BRAND_SVG_ADDITIONS } from "./connector-brand-svgs-additions";
import { GENERATED_BRAND_SVGS, type ConnectorBrandSvg } from "./connector-brand-svgs.generated";

export type { ConnectorBrandSvg };

/* The table the desktop app reads: the shared generated snapshot, newer thesvg
 * additions, and hand-maintained artwork corrections. The desktop bundle has no
 * separate Robinhood raster asset, so every available thesvg mark remains live. */
const BRAND_SVGS: Record<string, ConnectorBrandSvg> = {
  ...GENERATED_BRAND_SVGS,
  ...CONNECTOR_BRAND_SVG_ADDITIONS,
  ...CONNECTOR_BRAND_OVERRIDES,
};

const CONNECTOR_ALIASES: Readonly<Record<string, string>> = {
  chrome_devtools: "chrome",
  chrome_devtools_mcp: "chrome",
  drive: "gdrive",
  framer_mcp: "framer",
  github_mcp: "github",
  googlecalendar: "google_calendar",
  googledocs: "google_docs",
  googledrive: "gdrive",
  googlemeet: "google_meet",
  google_searchconsole: "google_search_console",
  googlesheets: "google_sheets",
  googleslides: "google_slides",
  googletasks: "google_tasks",
  microsoft_excel: "excel",
  microsoft_onedrive: "one_drive",
  microsoft_outlook: "outlook",
  microsoft_sharepoint: "share_point",
  microsoftteams: "microsoft_teams",
  producthunt: "product_hunt",
  robinhoodcard: "robinhood_card",
  surveymonkey: "survey_monkey",
  xcodebuildmcp: "xcode",
  xcodebuild_mcp: "xcode",
};

function normalizeConnectorIdentity(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]/gu, "")
    .replace(/[^a-z0-9]+/gu, "_")
    .replace(/^_+|_+$/gu, "");
}

function connectorCandidates(
  slug: string | null | undefined,
  name?: string | null,
): ReadonlyArray<string> {
  const candidates = new Set<string>();
  for (const value of [slug, name]) {
    if (!value) continue;
    const normalized = normalizeConnectorIdentity(value);
    if (!normalized) continue;
    candidates.add(normalized);
    const withoutMcp = normalized
      .replace(/^mcp_+/u, "")
      .replace(/_+(?:mcp|server|connector)$/u, "");
    if (withoutMcp) candidates.add(withoutMcp);
  }
  return [...candidates].flatMap((candidate) => [
    candidate,
    CONNECTOR_ALIASES[candidate] ?? candidate,
  ]);
}

/* Brand marks pasted from Illustrator/Figma/Inkscape often carry editor
 * metadata that never contributes pixels. Strip it at the read boundary before
 * encoding the SVG as an image URL. */
const svgCruftCache = new Map<string, string>();

function stripSvgEditorCruft(svg: string): string {
  const cached = svgCruftCache.get(svg);
  if (cached !== undefined) return cached;
  const cleaned = svg
    // Editor metadata blocks — RDF/Dublin-Core/Inkscape junk, never rendered.
    .replace(/<metadata\b[\s\S]*?<\/metadata>/gi, "")
    .replace(/<sodipodi:namedview\b[\s\S]*?(?:\/>|<\/sodipodi:namedview>)/gi, "")
    // Editor-only metadata attributes.
    .replace(/\sdata-[\w-]+="[^"]*"/gi, "")
    .replace(/\s(?:sodipodi|inkscape):[\w-]+="[^"]*"/gi, "")
    // Brand marks are decorative; the surrounding connector row supplies text.
    .replace(/\saria-[\w-]+="[^"]*"/gi, "")
    // The default xmlns stays; fragment references remain document-local.
    .replace(/\sxmlns:[\w-]+="[^"]*"/gi, "")
    // Snapshot root classes have no accompanying stylesheet.
    .replace(/(<svg\b[^>]*?)\sclass="[^"]*"/i, "$1")
    .trim();
  svgCruftCache.set(svg, cleaned);
  return cleaned;
}

/** Raw SVG string for a connector's brand mark, theme-selected, or undefined. */
export function getConnectorBrandSvg(
  slug: string | null | undefined,
  dark = false,
  name?: string | null,
): string | undefined {
  for (const candidate of connectorCandidates(slug, name)) {
    const entry = BRAND_SVGS[candidate];
    if (entry) return stripSvgEditorCruft(dark && entry.dark ? entry.dark : entry.light);
  }
  return undefined;
}

/** Whether a bundled thesvg brand mark exists for this connector. */
export function hasConnectorBrandSvg(
  slug: string | null | undefined,
  name?: string | null,
): boolean {
  return connectorCandidates(slug, name).some((candidate) => Boolean(BRAND_SVGS[candidate]));
}

/** All slugs with a bundled brand mark — used by the coverage test. */
export function connectorBrandSvgSlugs(): string[] {
  return Object.keys(BRAND_SVGS);
}
